import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { firebaseAuth, onIdTokenChanged, signInWithGoogle, firebaseSignOut, isAdmin, ADMIN_EMAIL } from "../lib/firebase";
import { toast } from "sonner";

/**
 * Global auth context. Wraps the whole app once (see `App.js`).
 *
 * Values exposed:
 *   user       — Firebase user object or null (unauthenticated).
 *   isAdmin    — boolean, true iff user.email === ADMIN_EMAIL.
 *   ready      — false while the initial auth state is still resolving;
 *                render nothing gated until this flips to true.
 *   idToken    — the latest ID token string (refreshed automatically by
 *                Firebase every ~50 min). Attached to every API request
 *                by the axios interceptor in `lib/api.js`.
 *   signIn()   — kicks off Google popup, resolves on success. Toasts on cancel.
 *   signOut()  — signs the current user out.
 */
const AuthCtx = createContext({
  user: null, isAdmin: false, ready: false, idToken: null,
  signIn: async () => {}, signOut: async () => {},
});

export const useAuth = () => useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const unsub = onIdTokenChanged(firebaseAuth(), async (u) => {
      setUser(u || null);
      if (u) {
        try {
          const t = await u.getIdToken();
          setIdToken(t);
          // Expose on window so the axios interceptor can read it without
          // needing the React context in scope.
          window.__firebase_id_token = t;
        } catch {
          setIdToken(null);
          window.__firebase_id_token = null;
        }
      } else {
        setIdToken(null);
        window.__firebase_id_token = null;
      }
      setReady(true);
    });
    return unsub;
  }, []);

  const value = useMemo(() => ({
    user,
    idToken,
    ready,
    isAdmin: isAdmin(user),
    signIn: async () => {
      try {
        await signInWithGoogle();
      } catch (e) {
        // 'auth/popup-closed-by-user' → silent
        if (e?.code !== "auth/popup-closed-by-user"
            && e?.code !== "auth/cancelled-popup-request") {
          toast.error(e?.message || "Sign-in failed");
        }
      }
    },
    signOut: async () => {
      await firebaseSignOut();
      toast("Signed out");
    },
  }), [user, idToken, ready]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

// Convenience re-export for pages that just want the admin email string.
export { ADMIN_EMAIL };
